import { useId } from "react";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";

import { useTranslation } from "@/translations";

import { ExchangeHistoryFilterBar } from "./components/ExchangeHistoryFilterBar";
import { ExchangeHistoryTable } from "./components/ExchangeHistoryTable";
import { useExchangeHistory } from "./hooks/useExchangeHistory";

interface Props {
  onClose: () => void;
}

/** The appointment exchange history (Tauschbörse – Geschichte): a fixed-height dialog listing the
 *  carried-out swaps, with a date + counselor filter bar and a scrollable read-only table. */
export function ExchangeHistoryPanel({ onClose }: Props) {
  const { t } = useTranslation();
  const titleId = useId();
  const {
    formik,
    rows,
    isLoading,
    serviceOptions,
    givenServiceFilter,
    setGivenServiceFilter,
    receivedServiceFilter,
    setReceivedServiceFilter,
    paginationResetKey,
  } = useExchangeHistory();

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby={titleId}
      slotProps={{ paper: { sx: { height: "85vh" } } }}
    >
      <DialogTitle
        id={titleId}
        variant="h1"
        sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        {t("exchange.history.title")}
        <IconButton aria-label={t("common.close")} onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 5, display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" }}>
        <ExchangeHistoryFilterBar formik={formik} />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ExchangeHistoryTable
            rows={rows}
            isLoading={isLoading}
            paginationResetKey={paginationResetKey}
            serviceOptions={serviceOptions}
            givenServiceFilter={givenServiceFilter}
            onGivenServiceFilterChange={setGivenServiceFilter}
            receivedServiceFilter={receivedServiceFilter}
            onReceivedServiceFilterChange={setReceivedServiceFilter}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
